import React, { useState, useEffect, useRef } from 'react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import api from '@/utils/api';
import { Send, Bot, User, RotateCcw, Upload, FileSpreadsheet, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useTenant } from '@/contexts/TenantContext';
import FittingResultCard from '@/components/fitting/FittingResultCard';

const CLUB_TYPE_OPTIONS = [
  { value: 'driver', label: 'Driver' },
  { value: 'fairway_wood', label: 'Fairway Wood' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: 'irons', label: 'Irons' },
];

export default function FittingWizardPage() {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStarting, setIsStarting] = useState(true);
  const [fittingType, setFittingType] = useState(null);
  const [recommendationResult, setRecommendationResult] = useState(null);
  const [fitProfile, setFitProfile] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showLaunchMonitorUpload, setShowLaunchMonitorUpload] = useState(false);
  const [lmClubType, setLmClubType] = useState('driver');
  const [lmFile, setLmFile] = useState(null);
  const [lmUploading, setLmUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const { tenantPath } = useTenant();

  useEffect(() => {
    // Check for existing session in localStorage
    const existingSession = localStorage.getItem('fittingWizardSession');
    if (existingSession) {
      loadExistingSession(existingSession);
    } else {
      setIsStarting(false);
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    // Scroll within the messages container only, not the whole page
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
    // Keep input focused for easy typing
    if (inputRef.current && !isLoading) {
      inputRef.current.focus();
    }
  };

  const loadExistingSession = async (id) => {
    try {
      const response = await api.get(`/fitting-wizard/session/${id}`);
      setSessionId(id);
      setMessages(response.data.messages || []);
      setFittingType(response.data.mode || 'existing');
      if (response.data.stage === 'done') {
        setFitProfile(response.data.fit_profile);
        setRecommendationResult(response.data.recommendation_result);
      } else if (response.data.stage === 'processing') {
        setIsLoading(true);
        pollForResult(id);
      }
    } catch (error) {
      // Session not found, start fresh
      localStorage.removeItem('fittingWizardSession');
    } finally {
      setIsStarting(false);
    }
  };

  const startNewSession = async (mode) => {
    try {
      setIsLoading(true);
      setFittingType(mode);

      const response = await api.post('/fitting-wizard/new-session', { mode });
      const newSessionId = response.data.session_id;

      setSessionId(newSessionId);
      localStorage.setItem('fittingWizardSession', newSessionId);
      setMessages([{
        role: 'assistant',
        content: response.data.opening_message,
        timestamp: new Date().toISOString()
      }]);
    } catch (error) {
      toast.error('Failed to start fitting session');
      setFittingType(null);
    } finally {
      setIsLoading(false);
    }
  };

  const startLaunchMonitorSession = async () => {
    if (!lmFile) {
      toast.error('Please choose a CSV or Excel file from your launch monitor or simulator first');
      return;
    }
    try {
      setLmUploading(true);
      const formData = new FormData();
      formData.append('club_type', lmClubType);
      formData.append('file', lmFile);

      const response = await api.post('/fitting-wizard/launch-monitor/new-session', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const newSessionId = response.data.session_id;
      setFittingType('launch_monitor');
      setSessionId(newSessionId);
      localStorage.setItem('fittingWizardSession', newSessionId);
      setMessages([{
        role: 'assistant',
        content: response.data.opening_message,
        timestamp: new Date().toISOString()
      }]);
      setShowLaunchMonitorUpload(false);
      toast.success(`Analyzed ${response.data.upload_summary.shot_count} shots from your session`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to analyze your session file');
    } finally {
      setLmUploading(false);
    }
  };

  const pollForResult = async (id) => {
    setIsAnalyzing(true);
    const maxAttempts = 40; // ~2 minutes of polling
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise((resolve) => setTimeout(resolve, 3000));
      try {
        const resp = await api.get(`/fitting-wizard/session/${id}`);
        const stage = resp.data.stage;
        if (stage === 'done') {
          setFitProfile(resp.data.fit_profile);
          setRecommendationResult(resp.data.recommendation_result);
          setIsLoading(false);
          setIsAnalyzing(false);
          return;
        }
        if (stage === 'error') {
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: resp.data.error_message || "Sorry, I couldn't complete your fitting - please try again.",
            timestamp: new Date().toISOString()
          }]);
          setIsLoading(false);
          setIsAnalyzing(false);
          toast.error('Fitting could not be completed');
          return;
        }
      } catch (error) {
        // Ignore transient poll errors, keep trying
      }
    }
    setIsLoading(false);
    setIsAnalyzing(false);
    toast.error('This is taking longer than expected - please try again shortly.');
  };

  const sendMessage = async (message) => {
    if (!message.trim() || !sessionId) return;

    // Add user message to UI immediately
    const userMessage = {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await api.post('/fitting-wizard/chat', {
        session_id: sessionId,
        message: message
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, assistantMessage]);

      if (response.data.stage === 'done') {
        setFitProfile(response.data.fitting_profile);
        setRecommendationResult(response.data.recommendation_result);
        setIsLoading(false);
      } else if (response.data.stage === 'processing') {
        // Stage 1 + Stage 2 run in the background (2 real LLM calls against
        // large schema-driven prompts routinely exceed the gateway's 60s
        // timeout) - poll the session until it flips to done/error, keep
        // isLoading true (and the typing indicator visible) while we wait.
        pollForResult(sessionId);
        return;
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      toast.error('Failed to send message');
      // Remove the user message if it failed
      setMessages(prev => prev.slice(0, -1));
      setIsLoading(false);
    } finally {
      // Refocus input after response
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;
    sendMessage(inputMessage);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const resetSession = async () => {
    if (sessionId) {
      try {
        await api.delete(`/fitting-wizard/session/${sessionId}`);
      } catch (error) {
        // Ignore errors
      }
    }
    localStorage.removeItem('fittingWizardSession');
    setSessionId(null);
    setMessages([]);
    setFittingType(null);
    setRecommendationResult(null);
    setFitProfile(null);
    setShowLaunchMonitorUpload(false);
    setLmFile(null);
  };

  if (isStarting) {
    return (
      <div className="min-h-screen bg-[#FAFAF8]">
        <Header />
        <div className="container mx-auto px-6 py-24 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#4A6741] border-r-transparent"></div>
          <p className="mt-4 text-[#5A6B5C]">Loading...</p>
        </div>
      </div>
    );
  }

  // Welcome screen - no active session or session only has upload (no chat yet)
  if (messages.length === 0 && !fittingType && !showLaunchMonitorUpload) {
    return (
      <div className="min-h-screen bg-[#FAFAF8]">
        <Header />
        
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-12">
          <div className="max-w-2xl mx-auto text-center">
            {/* Hero */}
            <div className="mb-12">
              <div className="w-20 h-20 bg-[#4A6741] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Bot className="h-10 w-10 text-white" />
              </div>
              <h1 
                className="text-4xl md:text-5xl font-bold text-[#2C3E2D] mb-4"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                AI – Club Fitting Wizard
              </h1>
              <p className="text-[#5A6B5C] text-lg max-w-lg mx-auto">
                Get personalized shaft recommendations based on your swing characteristics, playing style, and goals.
              </p>
            </div>

            {/* Fitting Type Selection */}
            <div className="mb-12">
              <h2 className="text-xl font-bold text-[#2C3E2D] mb-6">How would you like to get fit?</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Quick Fit Option */}
                <button
                  onClick={() => startNewSession('quick')}
                  disabled={isLoading}
                  className="bg-white p-8 rounded-xl border-2 border-[#E8E4DC] hover:border-[#4A6741] transition-all text-left group disabled:opacity-50"
                  data-testid="quick-fit-button"
                >
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-14 h-14 bg-[#D4A55A]/10 rounded-xl flex items-center justify-center group-hover:bg-[#D4A55A]/20 transition-colors">
                      <span className="text-3xl">⚡</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2C3E2D] text-lg">Quick Fit</h3>
                      <p className="text-[#D4A55A] text-sm font-medium">~2 minutes</p>
                    </div>
                  </div>
                  <p className="text-[#5A6B5C] text-sm">
                    Get a personalized shaft recommendation in about 2 minutes.
                  </p>
                </button>

                {/* Advanced Fit Option */}
                <button
                  onClick={() => startNewSession('advanced')}
                  disabled={isLoading}
                  className="relative bg-white p-8 rounded-xl border-2 border-[#E8E4DC] hover:border-[#4A6741] transition-all text-left group disabled:opacity-50"
                  data-testid="advanced-fit-button"
                >
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-14 h-14 bg-[#4A6741]/10 rounded-xl flex items-center justify-center group-hover:bg-[#4A6741]/20 transition-colors">
                      <span className="text-3xl">🎯</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2C3E2D] text-lg">Advanced Fit</h3>
                      <p className="text-[#4A6741] text-sm font-medium">~8-12 minutes</p>
                    </div>
                  </div>
                  <p className="text-[#5A6B5C] text-sm">
                    For experienced golfers who want a deeper fit based on delivery, feel and ball flight.
                  </p>
                </button>

                {/* Launch Monitor Fit Option */}
                <button
                  onClick={() => setShowLaunchMonitorUpload(true)}
                  disabled={isLoading}
                  className="relative bg-white p-8 rounded-xl border-2 border-[#E8E4DC] hover:border-[#4A6741] transition-all text-left group disabled:opacity-50"
                  data-testid="launch-monitor-fit-button"
                >
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-14 h-14 bg-[#2C3E2D]/10 rounded-xl flex items-center justify-center group-hover:bg-[#2C3E2D]/20 transition-colors">
                      <span className="text-3xl">📊</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-[#2C3E2D] text-lg">Launch Monitor Fit</h3>
                      <p className="text-[#2C3E2D] text-sm font-medium">Upload &amp; analyze</p>
                    </div>
                  </div>
                  <p className="text-[#5A6B5C] text-sm">
                    Upload your TrackMan or simulator session and we'll analyze your actual swing and ball-flight data.
                  </p>
                </button>
              </div>
            </div>

            {isLoading && (
              <div className="flex items-center justify-center space-x-3 text-[#5A6B5C]">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#4A6741] border-r-transparent"></div>
                <span>Starting your fitting session...</span>
              </div>
            )}

            <p className="text-[#5A6B5C] text-sm mt-8">
              Supports Driver, Fairway Wood, Hybrid, and Iron shaft fitting
            </p>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  // Launch Monitor upload panel - shown after clicking the Launch Monitor
  // Fit card, before any chat session/message exists (upload-first flow).
  if (messages.length === 0 && !fittingType && showLaunchMonitorUpload) {
    return (
      <div className="min-h-screen bg-[#FAFAF8]">
        <Header />

        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-12">
          <div className="max-w-xl mx-auto">
            <button
              onClick={() => setShowLaunchMonitorUpload(false)}
              className="flex items-center space-x-2 text-[#5A6B5C] hover:text-[#4A6741] transition-colors text-sm mb-8"
              data-testid="launch-monitor-back-button"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to fitting options</span>
            </button>

            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#2C3E2D] rounded-2xl flex items-center justify-center mx-auto mb-5">
                <FileSpreadsheet className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#2C3E2D] mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Upload Your Launch Monitor Session
              </h1>
              <p className="text-[#5A6B5C] text-sm max-w-md mx-auto">
                Works with TrackMan, FlightScope, GCQuad, Foresight, SkyTrak, Uneekor, Garmin R10, Mevo+, and Full Swing CSV/Excel exports.
              </p>
            </div>

            <div className="bg-white rounded-xl border-2 border-[#E8E4DC] p-6 space-y-6">
              <div>
                <label className="block text-sm font-bold text-[#2C3E2D] mb-3">Which club is this session for?</label>
                <div className="grid grid-cols-2 gap-2" data-testid="launch-monitor-club-type-selector">
                  {CLUB_TYPE_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setLmClubType(opt.value)}
                      className={`px-4 py-2.5 rounded-lg text-sm font-medium border-2 transition-all ${
                        lmClubType === opt.value
                          ? 'bg-[#4A6741] border-[#4A6741] text-white'
                          : 'bg-white border-[#E8E4DC] text-[#2C3E2D] hover:border-[#4A6741]'
                      }`}
                      data-testid={`launch-monitor-club-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-[#2C3E2D] mb-3">Session file (CSV or Excel)</label>
                <label
                  htmlFor="launch-monitor-file-input"
                  className="flex flex-col items-center justify-center border-2 border-dashed border-[#E8E4DC] rounded-lg py-8 px-4 cursor-pointer hover:border-[#4A6741] transition-colors"
                >
                  {lmFile ? (
                    <>
                      <FileSpreadsheet className="h-8 w-8 text-[#4A6741] mb-2" />
                      <span className="text-sm font-medium text-[#2C3E2D]" data-testid="launch-monitor-filename">{lmFile.name}</span>
                      <span className="text-xs text-[#5A6B5C] mt-1">Click to choose a different file</span>
                    </>
                  ) : (
                    <>
                      <Upload className="h-8 w-8 text-[#5A6B5C] mb-2" />
                      <span className="text-sm font-medium text-[#2C3E2D]">Click to choose a file</span>
                      <span className="text-xs text-[#5A6B5C] mt-1">.csv, .xlsx, or .xls</span>
                    </>
                  )}
                </label>
                <input
                  id="launch-monitor-file-input"
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  onChange={(e) => setLmFile(e.target.files?.[0] || null)}
                  data-testid="launch-monitor-file-input"
                />
              </div>

              <button
                onClick={startLaunchMonitorSession}
                disabled={lmUploading || !lmFile}
                className="w-full h-12 bg-[#4A6741] text-white rounded-lg font-medium hover:bg-[#3A5733] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                data-testid="launch-monitor-analyze-button"
              >
                {lmUploading ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-r-transparent"></div>
                    <span>Analyzing your session...</span>
                  </>
                ) : (
                  <span>Analyze My Session</span>
                )}
              </button>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  // Simple markdown renderer for chat messages
  const renderMarkdown = (text) => {
    // Split by bold markers and render
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  // Chat interface
  return (
    <div className="min-h-screen bg-[#FAFAF8] flex flex-col">
      <Header />
      
      <div className="flex-1 container mx-auto px-4 md:px-12 lg:px-24 py-6 flex flex-col max-w-4xl">
        {/* Chat Header */}
        <div className="bg-white rounded-t-xl border border-[#E8E4DC] border-b-0 p-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-[#4A6741] rounded-lg flex items-center justify-center">
              <Bot className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-[#2C3E2D]">Club Fitting Wizard</h2>
              <p className="text-xs text-[#5A6B5C]">AI-powered shaft recommendations</p>
            </div>
          </div>
          <button
            onClick={resetSession}
            className="flex items-center space-x-2 text-[#5A6B5C] hover:text-[#D4A55A] transition-colors text-sm"
            data-testid="reset-session-button"
          >
            <RotateCcw className="h-4 w-4" />
            <span>Start Over</span>
          </button>
        </div>

        {/* Messages */}
        <div 
          ref={messagesContainerRef}
          className="flex-1 bg-white border-x border-[#E8E4DC] overflow-y-auto p-4 space-y-4 min-h-[400px] max-h-[500px]"
        >
          {messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className="max-w-[85%]">
                <div
                  className={`rounded-2xl px-4 py-3 ${
                    message.role === 'user'
                      ? 'bg-[#4A6741] text-white rounded-br-md'
                      : 'bg-[#F5F3EF] text-[#2C3E2D] rounded-bl-md'
                  }`}
                  data-testid={`message-${message.role}-${index}`}
                >
                  <div className="flex items-start space-x-2">
                    {message.role === 'assistant' && (
                      <Bot className="h-4 w-4 mt-1 flex-shrink-0 text-[#4A6741]" />
                    )}
                    <p className="text-sm whitespace-pre-wrap">{renderMarkdown(message.content)}</p>
                    {message.role === 'user' && (
                      <User className="h-4 w-4 mt-1 flex-shrink-0" />
                    )}
                  </div>
                </div>
                
                {/* Structured final result (replaces the old markdown-parsed recommendation) */}
                {message.role === 'assistant' && index === messages.length - 1 && recommendationResult && (
                  <FittingResultCard
                    recommendationResult={recommendationResult}
                    fitProfile={fitProfile}
                    tenantPath={tenantPath}
                  />
                )}
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div className="bg-[#F5F3EF] rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex items-center space-x-2" data-testid="loading-indicator">
                  <Bot className="h-4 w-4 text-[#4A6741]" />
                  {isAnalyzing && <span className="text-xs text-[#5A6B5C]">Analyzing your fitting...</span>}
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-[#4A6741] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-[#4A6741] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-[#4A6741] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        {!recommendationResult && (
          <form onSubmit={handleSubmit} className="bg-white rounded-b-xl border border-[#E8E4DC] border-t-0 p-4">
            <div className="flex items-center space-x-3">
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your response..."
                disabled={isLoading}
                className="flex-1 h-12 px-4 bg-[#F5F3EF] border border-[#E8E4DC] rounded-lg text-[#2C3E2D] placeholder-[#5A6B5C] focus:outline-none focus:border-[#4A6741] disabled:opacity-50"
                data-testid="chat-input"
              />
              <button
                type="submit"
                disabled={isLoading || !inputMessage.trim()}
                className="h-12 w-12 bg-[#4A6741] text-white rounded-lg hover:bg-[#3A5733] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                data-testid="send-button"
              >
                <Send className="h-5 w-5" />
              </button>
            </div>
          </form>
        )}
        {recommendationResult && (
          <div className="bg-white rounded-b-xl border border-[#E8E4DC] border-t-0 p-4 flex items-center justify-center">
            <button
              onClick={resetSession}
              className="flex items-center space-x-2 text-sm font-medium text-[#4A6741] hover:text-[#3A5733] transition-colors"
              data-testid="start-new-fitting-button"
            >
              <RotateCcw className="h-4 w-4" />
              <span>Start a New Fitting</span>
            </button>
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
}
